// demoDailyCosts — one cost-log report per drilling day, for the Permian demo pad.
//
// WHY. The demo pad had a schematic, a trajectory and (since today) a time log, but every cost log
// opened BLANK. PO 2026-09-03: "I want to get the full corpus here for demo purposes. no more blank
// reports." A cost log is the first thing a drilling engineer opens after the time log, and an
// empty one makes the whole AFE / actual-vs-budget story unavailable.
//
// ── SPEND FOLLOWS THE OPERATION ───────────────────────────────────────────────────────────────
//
// Every line on a day is justified by what the rig was DOING that day, read from
// `demoWellProgram`'s day view — which is itself a view of the PO's ratified operational sequence
// (DECISIONS.md 2026-09-03). Nothing is sprinkled at random. A cementing day carries a cement
// crew, cement and a pump charge; it does not carry a wireline unit. A day with NPT on it carries
// the trouble-cost code. That is what makes the log survive being read by someone who knows the
// work: the costs and the time log tell the SAME story, because they read the same source.
//
// ── WHAT THIS FILE DELIBERATELY DOES NOT DO (Charter Abs-2) ───────────────────────────────────
//
// No engineering, economic or statistical math. It writes the two fields an engineer types —
// `costPerUnit` and `units` — plus the coding dimensions the picklists already offer. Every
// rollup, variance, allocation and AFE reconciliation stays in Python. The one arithmetic call is
// `calculateTotalCost` — the existing helper in `CostLogForm/utils.ts`, which already carries its
// own exemption marker. It is reused rather than reimplemented so the stored day total cannot drift
// from the form's.
//
// THE THREE `units:` COEFFICIENTS BELOW (bbl of mud per metre, bbl of water per metre, sacks of
// cement per metre) were flagged by adversarial review as arguably Engineering-category math. The
// PO ruled them display arithmetic on 2026-09-03 and this file is now on the enumerated carve-out
// site list; each site carries its own marker. Read that ledger entry before extending the pattern
// — it explicitly does NOT cover product code, and S3's mud hydraulics do not inherit it.
//
// Rates are AUTHORED DEMO FIGURES in 2018-era US land dollars. They are not a quote, a benchmark or
// an AFE basis, and nothing may cite them as one (Charter Abs-1 — no uncited engineering). They
// exist to make the shape of a well's spend legible: a dayrate that dominates, tangibles that
// spike on casing days, services that come and go with the operation.
//
// THE MAGNITUDE IS THE PO'S CALL, NOT AN AGENT'S. A first version came to ~$3.1M spud-to-release
// and he judged it too low; the rates and the omitted lines below were set from that ruling. See
// DECISIONS.md 2026-09-03, "The demo well's drilling cost lands at $4-5M, not $3M". Well 1 now
// totals $4,165,627 over 25 days at 35% tangible — reproduce with the "lands in a defensible
// band" and IDC/TDC assertions in demoDailyCosts.test.ts (`npx vitest run src/utils/`).

import type { Report } from "../features/reports/types";
import type {
  CostLogEntry,
  CostLogReportData,
} from "../features/reports/forms/CostLogForm/types";
import { calculateTotalCost } from "../features/reports/forms/CostLogForm/utils";
import { COST_CODE_TAX_DEFAULTS } from "../features/reports/forms/CostLogForm/constants";
import type { CostLogCode1 } from "../features/reports/forms/CostLogForm/constants";
import {
  seededFloat,
  seededInt,
  seededPick,
  INTERVAL_FOR_SECTION,
  PHASE_FOR_SECTION,
  type DemoDay,
  type DemoWellProgram,
} from "./demoWellProgram";

/** Vendors REPEAT — an operator uses one cementer, one mud company, one directional provider for a
 *  campaign. Picking a fresh vendor per line is the tell that data is synthetic.
 *
 *  SCOPE, stated precisely because an earlier comment claimed "one per service per PAD" while the
 *  seed carries no pad or well component: it is one vendor per service GLOBALLY — the same
 *  cementer on every well of every pad. For a single demo campaign that is the intended behaviour;
 *  a second pad wanting its own vendor set would add the pad to the seed. */
const VENDORS = {
  rig: ["Patterson-UTI", "Nabors Industries", "Precision Drilling"],
  mud: ["M-I SWACO", "Baroid", "Newpark Resources"],
  directional: ["Halliburton Sperry", "Schlumberger", "Scout Downhole"],
  cement: ["Halliburton", "Schlumberger", "BJ Services"],
  casing: ["Frank's International", "Weatherford", "Tenaris"],
  wireline: ["Halliburton", "Schlumberger", "Superior Energy"],
  water: ["Select Energy", "Rockwater", "Layne Water"],
  rentals: ["Superior Energy", "Basic Energy", "Key Energy"],
  trucking: ["Aveda Transportation", "Kenan Advantage", "Bosque Systems"],
  labor: ["Cudd Energy", "Basic Energy", "RWLS"],
} as const;

type VendorKey = keyof typeof VENDORS;

/** A cost line TEMPLATE. `units` is resolved per day so a dayrate bills 1 day, a casing run bills
 *  its metres, and a mud order bills the barrels the hole actually took. */
interface LineTemplate {
  /** STABLE IDENTITY for seeding and entry ids. Deliberately NOT the catalogue index: inserting a
   *  line anywhere in `CATALOGUE` would renumber every entry below it, changing every stored rate
   *  and every entry id — in data whose whole purpose is to be screenshot-able and assertable. */
  key: string;
  code1: CostLogCode1;
  code2: string;
  comment: string;
  vendor: VendorKey;
  /** $/unit, as a [min,max] band the seed picks from — steady per well, not per day. Optional
   *  only when `rateFor` always supplies one. */
  rate?: [number, number];
  /** Section-dependent band, for items whose price is set by WHICH string is being run: 13-3/8"
   *  surface pipe, 9-5/8" intermediate and 5-1/2" production do not cost the same per metre, and a
   *  production cement job is several times a surface job. Overrides `rate` when present. */
  rateFor?: (day: DemoDay) => [number, number];
  uom: string;
  /** How many units this line bills on a given day. */
  units: (day: DemoDay, ctx: DayContext) => number;
  /** A dayrate-shaped row rolls forward to the next day. `CostLogEntry.carryForward` in
   *  CostLogForm/types.ts is the field of record; its own doc comment attributes the flag to the
   *  PO on 2026-07-11, but NO ledger entry of that date exists, so this file points at the type
   *  rather than restating the attribution (CLAUDE.md: an in-code attribution is not
   *  authoritative). */
  carryForward?: boolean;
  isRental?: boolean;
  /** ONE-OFF PURCHASES BILL ONCE PER EVENT, not once per day the event touches. A casing run
   *  spans two days, so a `when` that merely tests "did casing happen today" bought the string
   *  TWICE — $910k on day 21 and $908k again on day 22. When set, the line bills only on the
   *  FIRST day of each contiguous run of days carrying any of these codes. */
  oncePerEvent?: string[];
  /** ONE PER HOLE SECTION. Stronger than `oncePerEvent`, and correct for anything bought per
   *  string: a casing run interrupted by a gap day breaks contiguity, and contiguity-based event
   *  detection then bought the SAME string twice ($259k for one 610 m surface string, reproduced
   *  against an injected sequence). A section is an identity; a contiguous run is a coincidence. */
  oncePerSection?: boolean;
  /** Resolves the code2 line item from the day, for lines whose subcode depends on which string
   *  is being run. */
  code2For?: (day: DemoDay) => string;
}

interface DayContext {
  /** Metres of new hole the day made. */
  metres: number;
  /** Critical-path hours by coding-template code1. */
  hours: Record<string, number>;
  wellIndex: number;
}

/** Did any of these codes TOUCH the day?
 *
 *  Tests key PRESENCE, not hours. A milestone (PLUG_BUMPED, RIG_RELEASE, CASING_POINT, SPUD,
 *  TD_REACHED, FIT_LOT_ACHIEVED) is instantaneous — `startH === endH` — so it contributes zero
 *  hours. An earlier version tested `hours > 0` and therefore never saw one: cementing services,
 *  cement materials and rig demobilization generated ZERO lines for an entire well, and the
 *  omission was invisible because a missing line looks exactly like a line that was not due. */
const has = (ctx: DayContext, ...codes: string[]): boolean =>
  codes.some((c) => c in ctx.hours);
const hoursOf = (ctx: DayContext, ...codes: string[]): number =>
  codes.reduce((s, c) => s + (ctx.hours[c] ?? 0), 0);

// ── The line catalogue ────────────────────────────────────────────────────────────────────────
//
// Each entry says WHEN it appears (`when`) and WHAT it bills (`units`). The `when` predicates are
// the whole point: they read the day's own hours, so the catalogue cannot put a cementer on a
// drilling day.

interface CatalogueEntry extends LineTemplate {
  when: (day: DemoDay, ctx: DayContext) => boolean;
}

/** The code that marks an actual CEMENT JOB.
 *
 *  Deliberately EXCLUDES `"cement"`, which despite its name is the CBL/VDL evaluation milestone
 *  (`code2: "cbl"`, emitted once at TD) and is not pumping at all. Including it billed a FOURTH
 *  cement job on a three-string well. The CBL is billed below as the cased-hole log it is.
 *
 *  Also excludes `RU_CEMENT`, `PLUG_BUMPED` and `WOC_COMPLETE`: those are the same job's rig-up and
 *  tail, and keying on them widens the event across a day boundary for no gain. */
/** The identity of a cement JOB is the pumping block, not the hole section it sits in and not a
 *  contiguous run of days. Section identity is wrong here: a job's `WOC_COMPLETE` tail lands the
 *  morning after the pump, by which time the day view has already advanced to the NEXT section, so
 *  a three-string well billed four cement jobs. `CEMENT_JOB` occurs exactly once per string. */
const CEMENT_JOB_CODE = ["CEMENT_JOB"];

/** Every code the sequence uses to mark a casing/formation pressure test. */
const PRESSURE_TEST_CODES = [
  "FIT",
  "FIT_LOT_ACHIEVED",
  "CSG_TEST",
  "CSG_PT_HIGH",
  "CSG_PT_LOW",
  "CSG_TEST_WITNESSED",
];

const CATALOGUE: CatalogueEntry[] = [
  // ── Every day the rig is on location ────────────────────────────────────────────────────────
  {
    key: "drilling-rig-dayrate-crew",
    code1: "1002",
    code2: "100201",
    comment: "Drilling rig dayrate & crew",
    vendor: "rig",
    rate: [21_000, 26_000],
    uom: "day",
    carryForward: true,
    when: () => true,
    units: () => 1,
  },
  {
    key: "company-rep-drilling-supervisor",
    code1: "1002",
    code2: "100207",
    comment: "Company rep / drilling supervisor",
    vendor: "labor",
    rate: [2_050, 2_650],
    uom: "day",
    carryForward: true,
    when: () => true,
    units: () => 1,
  },
  {
    key: "diesel-rig-power",
    code1: "1002",
    code2: "100206",
    comment: "Diesel — rig power",
    vendor: "trucking",
    rate: [2.05, 2.55],
    uom: "gal",
    carryForward: true,
    when: () => true,
    // A rig burns hard while drilling and idles otherwise; the day view already knows which.
    units: (d, ctx) =>
      d.flat
        ? seededInt(1_300, 1_900, "fuel", ctx.wellIndex, d.dayNo)
        : seededInt(2_600, 3_500, "fuel", ctx.wellIndex, d.dayNo),
  },
  {
    key: "safety-environmental-communications",
    code1: "1007",
    code2: "100703",
    comment: "Safety, environmental & communications",
    vendor: "labor",
    rate: [340, 480],
    uom: "day",
    carryForward: true,
    when: () => true,
    units: () => 1,
  },

  {
    key: "trucking-crane-location-support",
    code1: "1007",
    code2: "100701",
    comment: "Trucking, crane & location support",
    vendor: "trucking",
    rate: [1_800, 2_700],
    uom: "day",
    carryForward: true,
    when: () => true,
    units: () => 1,
  },
  {
    key: "roustabout-crew-location-labor",
    code1: "1007",
    code2: "100702",
    comment: "Roustabout crew & location labor",
    vendor: "labor",
    rate: [1_250, 1_900],
    uom: "day",
    when: (d) => d.section !== "mobilization" || d.dayNo > 1,
    units: () => 1,
  },

  // ── Mobilization only ───────────────────────────────────────────────────────────────────────
  {
    key: "rig-camp-mobilization-to-pad",
    code1: "1001",
    code2: "100102",
    comment: "Rig & camp mobilization to pad",
    vendor: "trucking",
    // Moving a Delaware pad rig is 40-60 truckloads plus crane time, not a service call.
    rate: [155_000, 240_000],
    uom: "lot",
    // Move-in is ONE charge, not one per mobilization day — it billed three times.
    when: (d) => d.dayNo === 1,
    units: () => 1,
  },
  {
    key: "drilling-rentals-tanks-tools-matting",
    code1: "1002",
    code2: "100205",
    comment: "Drilling rentals — tanks, tools, matting",
    vendor: "rentals",
    rate: [4_100, 6_000],
    uom: "day",
    carryForward: true,
    isRental: true,
    when: (d) => d.section !== "mobilization" || d.dayNo > 1,
    units: () => 1,
  },

  // ── Making hole ─────────────────────────────────────────────────────────────────────────────
  {
    key: "drilling-fluids-chemicals",
    code1: "1002",
    code2: "100202",
    comment: "Drilling fluids & chemicals",
    vendor: "mud",
    rate: [28, 44],
    uom: "bbl",
    // Mud is bought to fill new hole and to treat what circulates. Metres drilled is the driver.
    when: (_d, ctx) => ctx.metres > 0 || has(ctx, "CIRC_COND"),
    // FE-MATH-EXEMPT: authored demo coefficient sizing a synthetic cost line — never fitted, never
    // compared to a measurement, never read back by any calculation — PO-approved 2026-09-03
    // Oil-based mud on a two-mile lateral: hole volume plus losses, plus what conditioning treats.
    units: (_d, ctx) =>
      Math.max(
        40,
        Math.round(ctx.metres * 0.95 + hoursOf(ctx, "CIRC_COND") * 14),
      ),
  },
  {
    key: "directional-mwd-motor",
    code1: "1002",
    code2: "100203",
    comment: "Directional, MWD & motor",
    vendor: "directional",
    rate: [12_500, 17_500],
    uom: "day",
    carryForward: true,
    // Directional is on the ticket from the kickoff to TD, and while surveying.
    when: (d, ctx) =>
      d.section === "curve" || d.section === "lateral" || has(ctx, "SURVEY"),
    units: () => 1,
  },
  {
    key: "mud-logging-unit",
    code1: "1002",
    code2: "100209",
    comment: "Mud logging unit",
    vendor: "mud",
    rate: [1_650, 2_400],
    uom: "day",
    carryForward: true,
    isRental: true,
    when: (d) => d.section !== "mobilization",
    units: () => 1,
  },
  {
    key: "solids-control-drilling-waste-haul-off",
    code1: "1002",
    code2: "100210",
    comment: "Solids control & drilling-waste haul-off",
    vendor: "rentals",
    rate: [3_400, 5_200],
    uom: "day",
    when: (_d, ctx) => ctx.metres > 0,
    units: () => 1,
  },
  {
    key: "water-sourcing-swd-disposal",
    code1: "1002",
    code2: "100212",
    comment: "Water sourcing & SWD disposal",
    vendor: "water",
    rate: [1.15, 1.85],
    uom: "bbl",
    when: (_d, ctx) => ctx.metres > 0 || has(ctx, "CIRC_COND"),
    // FE-MATH-EXEMPT: authored demo coefficient sizing a synthetic cost line — never fitted, never
    // compared to a measurement, never read back by any calculation — PO-approved 2026-09-03
    // Delaware drilling water is a real line: sourcing, hauling and SWD disposal of returns.
    units: (_d, ctx) =>
      Math.max(
        250,
        Math.round(ctx.metres * 2.7 + hoursOf(ctx, "CIRC_COND") * 60),
      ),
  },
  {
    // Bits are a TANGIBLE-adjacent consumable but sit under drilling services in this code book;
    // billed when a bit actually goes in the hole, which is what a BHA make-up day is.
    key: "bit-bha-components",
    code1: "1002",
    code2: "100203",
    comment: "Bit & BHA components",
    vendor: "directional",
    rate: [28_000, 46_000],
    uom: "each",
    oncePerEvent: ["BHA_MAKEUP"],
    when: (_d, ctx) => has(ctx, "BHA_MAKEUP"),
    units: () => 1,
  },

  // ── Tripping ────────────────────────────────────────────────────────────────────────────────
  {
    key: "tubular-handling-pipe-handling-tools",
    code1: "1002",
    code2: "100205",
    comment: "Tubular handling & pipe-handling tools",
    vendor: "rentals",
    rate: [1_400, 2_100],
    uom: "day",
    isRental: true,
    when: (_d, ctx) => has(ctx, "TRIPS"),
    units: () => 1,
  },

  // ── Casing and cement — the tangible spikes ─────────────────────────────────────────────────
  {
    key: "casing-string",
    code1: "1014",
    code2: "101404",
    // The string bought depends on which section is being cased — the code book has a distinct
    // line item for each, and a cost log that codes every string as "production" is unusable for
    // the tangible rollup.
    code2For: (d) =>
      d.section === "surface"
        ? "101402"
        : d.section === "intermediate"
          ? "101403"
          : "101404",
    comment: "Casing string",
    vendor: "casing",
    rate: [95, 165],
    // 13-3/8" surface and 9-5/8" intermediate cost more per metre than 5-1/2" production pipe;
    // a single flat $/m made the production string look like the expensive one.
    rateFor: (d) =>
      d.section === "surface"
        ? [175, 235]
        : d.section === "intermediate"
          ? [140, 195]
          : [95, 140],
    uom: "m",
    oncePerSection: true,
    when: (_d, ctx) => has(ctx, "RUN_CASING", "CASING_PREP", "CASING_POINT"),
    // The string is bought once for the section, billed on the day it is run, by its length.
    units: (d) => Math.max(200, Math.round(d.depthTo)),
  },
  {
    // Centralizers, float equipment, stage tools and thread compound — a real tangible line the
    // first version folded silently into the string price.
    key: "centralizers-float-equipment-casing-accessor",
    code1: "1014",
    code2: "101404",
    code2For: (d) =>
      d.section === "surface"
        ? "101402"
        : d.section === "intermediate"
          ? "101403"
          : "101404",
    comment: "Centralizers, float equipment & casing accessories",
    vendor: "casing",
    rate: [9, 16],
    uom: "m",
    oncePerSection: true,
    when: (_d, ctx) => has(ctx, "RUN_CASING", "CASING_PREP", "CASING_POINT"),
    units: (d) => Math.max(200, Math.round(d.depthTo)),
  },
  {
    key: "casing-crew-running-services",
    code1: "1002",
    code2: "100208",
    comment: "Casing crew & running services",
    vendor: "casing",
    rate: [11_000, 16_500],
    uom: "day",
    when: (_d, ctx) => has(ctx, "RUN_CASING", "CASING_PREP"),
    units: () => 1,
  },
  {
    key: "cementing-services-pumping-equipment",
    code1: "1002",
    code2: "100204",
    comment: "Cementing services — pumping & equipment",
    vendor: "cement",
    rate: [18_000, 27_000],
    rateFor: (d) =>
      d.section === "surface"
        ? [22_000, 32_000]
        : d.section === "intermediate"
          ? [48_000, 68_000]
          : [78_000, 115_000],
    uom: "lot",
    oncePerEvent: CEMENT_JOB_CODE,
    when: (_d, ctx) => has(ctx, ...CEMENT_JOB_CODE),
    units: () => 1,
  },
  {
    key: "cement-additives-spacer",
    code1: "1002",
    code2: "100211",
    comment: "Cement, additives & spacer",
    vendor: "cement",
    rate: [17, 26],
    uom: "sk",
    oncePerEvent: CEMENT_JOB_CODE,
    when: (_d, ctx) => has(ctx, ...CEMENT_JOB_CODE),
    // FE-MATH-EXEMPT: authored demo coefficient sizing a synthetic cost line — never fitted, never
    // compared to a measurement, never read back by any calculation — PO-approved 2026-09-03
    units: (d) => Math.max(300, Math.round(d.depthTo * 0.42)),
  },
  {
    key: "wellhead-casing-head",
    code1: "1015",
    code2: "101501",
    comment: "Wellhead & casing head",
    vendor: "casing",
    rate: [34_000, 52_000],
    uom: "each",
    oncePerEvent: ["CSG_NU", "BOP_NIPPLED_UP"],
    when: (_d, ctx) => has(ctx, "CSG_NU", "BOP_NIPPLED_UP"),
    units: () => 1,
  },

  // ── Pressure control ────────────────────────────────────────────────────────────────────────
  {
    key: "bop-pressure-control-rental",
    code1: "1003",
    code2: "100304",
    comment: "BOP & pressure-control rental",
    vendor: "rentals",
    rate: [2_600, 3_900],
    uom: "day",
    carryForward: true,
    isRental: true,
    when: (d) => d.section !== "mobilization",
    units: () => 1,
  },
  {
    key: "fit-lot-casing-pressure-test",
    code1: "1005",
    code2: "100504",
    comment: "FIT / LOT & casing pressure test",
    vendor: "wireline",
    rate: [4_200, 6_800],
    uom: "lot",
    // "CSG_TEST" was MISSING and is the one the production string actually emits: that string is
    // run with `drillAhead: false`, so `pushCasingCycle` writes a CSG_TEST block and no
    // FIT_LOT_ACHIEVED milestone. A three-string well billed two pressure tests, every time.
    oncePerEvent: PRESSURE_TEST_CODES,
    when: (_d, ctx) => has(ctx, ...PRESSURE_TEST_CODES),
    units: () => 1,
  },

  // ── Evaluation ──────────────────────────────────────────────────────────────────────────────
  {
    key: "open-hole-wireline-logging",
    code1: "1005",
    code2: "100501",
    comment: "Open-hole wireline logging",
    vendor: "wireline",
    rate: [46_000, 72_000],
    uom: "lot",
    oncePerEvent: ["WIRELINE_LOGS"],
    when: (_d, ctx) => has(ctx, "WIRELINE_LOGS"),
    units: () => 1,
  },
  {
    // The CBL/VDL. The sequence marks it `code1: "cement", code2: "cbl"` — a name that reads like
    // pumping and is not; billing it here puts it where the code book actually files it.
    key: "cased-hole-cement-bond-log",
    code1: "1005",
    code2: "100502",
    comment: "Cased-hole logging — CBL/VDL",
    vendor: "wireline",
    rate: [21_000, 32_000],
    uom: "lot",
    oncePerEvent: ["cement"],
    when: (_d, ctx) => has(ctx, "cement"),
    units: () => 1,
  },
  {
    key: "definitive-gyro-survey",
    code1: "1005",
    code2: "100505",
    comment: "Definitive gyro survey",
    vendor: "directional",
    rate: [14_000, 21_000],
    uom: "lot",
    oncePerEvent: ["CALIBRATION"],
    when: (_d, ctx) => has(ctx, "CALIBRATION"),
    units: () => 1,
  },

  // ── Trouble ─────────────────────────────────────────────────────────────────────────────────
  {
    key: "lost-circulation-material-remediation",
    code1: "1003",
    code2: "100303",
    comment: "Lost circulation material & remediation",
    vendor: "mud",
    rate: [2_400, 6_500],
    uom: "lot",
    oncePerEvent: ["NPT"],
    when: (_d, ctx) => has(ctx, "NPT"),
    units: () => 1,
  },
  {
    key: "trouble-cost-reserve-draw-unscheduled-downti",
    code1: "1009",
    code2: "100901",
    comment: "Trouble-cost reserve draw — unscheduled downtime",
    vendor: "rig",
    rate: [900, 1_150],
    uom: "hr",
    when: (_d, ctx) => has(ctx, "NPT"),
    units: (_d, ctx) => Math.round(hoursOf(ctx, "NPT")),
  },

  // ── Demobilization ──────────────────────────────────────────────────────────────────────────
  {
    key: "rig-demobilization",
    code1: "1001",
    code2: "100101",
    comment: "Rig demobilization",
    vendor: "trucking",
    rate: [85_000, 130_000],
    uom: "lot",
    when: (_d, ctx) => has(ctx, "RIG_RELEASE"),
    units: () => 1,
  },
];

/**
 * Build one cost-log report per day of a well's drilling job.
 *
 * @param program  the day view (which is a view of the ratified time-log sequence)
 * @param wellName used only to seed vendor selection per well
 */
export function generateDemoCostLogReports(
  program: DemoWellProgram,
  wellName: string,
): Report[] {
  const reports: Report[] = [];

  // EVENT STARTS, precomputed once. A `oncePerEvent` line bills only on the first day of each
  // contiguous run of days carrying one of its codes — so a two-day casing run buys one string,
  // and a well that runs three strings buys three.
  // Keyed on the TEMPLATE, not on its code list. Two templates sharing a code list but differing
  // in `when` used to share one Set — and because the Set was built from the codes ALONE, a `when`
  // that was false on the event's first day deleted the line entirely rather than moving it. That
  // failure mode is silent and total: narrowing one `when` in a scratch copy removed all three
  // casing strings and both accessory sets, $1.4M of the well, with no error.
  const eventStartDays = new Map<string, Set<number>>();
  const dayCtx = (d: DemoDay): DayContext => ({
    metres: d.depthTo - d.depthFrom,
    hours: d.hoursByCode,
    wellIndex: program.wellIndex,
  });
  for (const tpl of CATALOGUE) {
    if (!tpl.oncePerEvent && !tpl.oncePerSection) continue;
    const starts = new Set<number>();
    if (tpl.oncePerSection) {
      // One per hole section — an identity, immune to a gap day inside the run.
      const claimed = new Set<string>();
      for (const d of program.days) {
        if (!tpl.when(d, dayCtx(d))) continue;
        if (claimed.has(d.section)) continue;
        claimed.add(d.section);
        starts.add(d.dayNo);
      }
    } else {
      let inEvent = false;
      for (const d of program.days) {
        // Key presence, not hours — see `has`. A cement job is marked by PLUG_BUMPED, a milestone.
        const active =
          tpl.oncePerEvent!.some((c) => c in d.hoursByCode) &&
          tpl.when(d, dayCtx(d));
        if (active && !inEvent) starts.add(d.dayNo);
        inEvent = active;
      }
    }
    eventStartDays.set(tpl.key, starts);
  }

  for (const day of program.days) {
    const ctx: DayContext = {
      metres: day.depthTo - day.depthFrom,
      hours: day.hoursByCode,
      wellIndex: program.wellIndex,
    };

    const entries: CostLogEntry[] = [];
    for (const tpl of CATALOGUE) {
      if (!tpl.when(day, ctx)) continue;
      if (
        (tpl.oncePerEvent || tpl.oncePerSection) &&
        !eventStartDays.get(tpl.key)!.has(day.dayNo)
      ) {
        continue;
      }
      const units = tpl.units(day, ctx);
      if (units <= 0) continue;

      // The rate is seeded on the WELL, not the day — a vendor's dayrate does not change
      // overnight. The vendor is seeded on the PAD, so one cementer serves the campaign.
      const band = tpl.rateFor ? tpl.rateFor(day) : tpl.rate!;
      const rate = seededFloat(band[0], band[1], 2, "rate", wellName, tpl.key);
      const vendorName = seededPick(VENDORS[tpl.vendor], "vendor", tpl.vendor);

      entries.push({
        id: `${wellName}-cost-${day.dayNo}-${tpl.key}`,
        costPerUnit: String(rate),
        units: String(units),
        code1: tpl.code1,
        code2: tpl.code2For ? tpl.code2For(day) : tpl.code2,
        interval: INTERVAL_FOR_SECTION[day.section],
        phase: PHASE_FOR_SECTION[day.section],
        activity: activityLabel(day),
        comment: tpl.comment,
        ...(tpl.carryForward ? { carryForward: true } : {}),
        ...(tpl.isRental ? { isRental: true } : {}),
        vendorName,
        uom: tpl.uom,
        // Seeded from the code book's DRAFT map — the same defaulting the form does when a user
        // picks a code, so a generated line classifies exactly as a typed one would. Not
        // authoritative; accounting still has to ratify that map (Charter Abs-5).
        taxClass: COST_CODE_TAX_DEFAULTS[tpl.code1] ?? "",
      });
    }

    const data: CostLogReportData = {
      entries,
      date: day.date,
      // The existing marked helper, reused rather than reimplemented, so a stored day total
      // cannot drift from what the form computes on open. See the header note on carve-out sites.
      totalCost: calculateTotalCost(entries),
      source: "demo-generator",
    };

    reports.push({
      id: `${wellName}-cost-log-${day.dayNo}`,
      reportType: "cost-log",
      reportNumber: day.dayNo,
      date: day.date,
      data,
    } as unknown as Report);
  }

  return reports;
}

/** The cost log's `activity` picklist is coarser than the time log's coding template, so the day's
 *  code1 is mapped onto it. An unmapped code becomes "Other" rather than inventing a label the
 *  picklist cannot render. */
function activityLabel(day: DemoDay): string {
  const map: Record<string, string> = {
    DRILLING: "Drilling",
    REAMING: "Drilling",
    TRIPS: "Tripping",
    RUN_CASING: "Casing",
    CASING_PREP: "Casing",
    CASING_POINT: "Casing",
    CEMENT_JOB: "Cementing",
    RU_CEMENT: "Cementing",
    PLUG_BUMPED: "Cementing",
    WOC_COMPLETE: "Cementing",
    // `"cement"` is the CBL/VDL milestone, not pumping — see CEMENT_JOB_CODE.
    cement: "Logging",
    CSG_TEST: "Testing",
    CIRC_COND: "Circulating",
    WIRELINE_LOGS: "Logging",
    CALIBRATION: "Logging",
    SURVEY: "Logging",
    FIT: "Testing",
    CSG_PT_HIGH: "Testing",
    CSG_PT_LOW: "Testing",
    CSG_NU: "Rig Up / Down",
    BOP_NIPPLED_UP: "Rig Up / Down",
    RIG_DOWN: "Rig Up / Down",
    RIG_RELEASE: "Rig Up / Down",
    RIG_INSP: "Rig Up / Down",
    SVC_RIG: "Rig Repair",
    NPT: "Waiting (NPT)",
    BHA_MAKEUP: "Tripping",
    SPUD: "Drilling",
    TD_REACHED: "Drilling",
  };
  if (day.section === "mobilization") return "Rig Up / Down";
  return map[day.activity] ?? "Other";
}
